<script setup lang="ts">
import { ApiClientError } from '@shopware/api-client'

interface AccountLoginProps {
    disabled?: boolean
}

interface AccountLoginEmits {
    (e: 'login-success' | 'forgot-password' | 'switch-to-register'): void
}

const props = withDefaults(defineProps<AccountLoginProps>(), {
    disabled: false
})

const emit = defineEmits<AccountLoginEmits>()

// Composables
const { login, isLoggedIn } = useUser()
const { resetPassword } = useCustomerPassword()
const { resolveApiErrors } = useApiErrorsResolver('account_login')
const { success } = useGlobalNotifications()
const { t } = useI18n()
const { recaptcha } = useRecaptchaV3()
const { close: closeSidenav } = useSidenav()
const storefrontUrl = useRuntimeConfig().public.shopware?.devStorefrontUrl as string || useRequestURL().origin

// State
const isLoading = ref(false)
const isPasswordRecoveryMode = ref(false)
const errors = ref<string[]>([])

const formData = reactive({
    email: '',
    password: ''
})

// Computed
const isFormValid = computed(() => {
    if (isPasswordRecoveryMode.value) {
        return formData.email.length > 0 && !isLoading.value
    }
    return formData.email.length > 0 && 
           formData.password.length > 0 && 
           !isLoading.value
})

// Methods
const verifyRecaptcha = async (): Promise<boolean> => {
    const isNotABot = await recaptcha()

    if (!isNotABot) {
        console.error('Recaptcha failed, please try again.')
        isLoading.value = false
    }

    return isNotABot
}
const handleLogin = async () => {
    if (!isFormValid.value || props.disabled) return

    errors.value = []
    isLoading.value = true
    if (!await verifyRecaptcha()) return

    try {
        await login({
            username: formData.email,
            password: formData.password
        })

        // Check if login was successful
        if (isLoggedIn.value) {
            emit('login-success')
        } else {
            // Login function completed but user is not logged in
            errors.value = ['Login failed. Please check your credentials.']
        }
    } catch (error) {
        if (error instanceof ApiClientError) {
            errors.value = resolveApiErrors(error.details.errors)
        } else {
            errors.value = [error instanceof Error ? error.message : 'An unknown error occurred']
        }
    } finally {
        isLoading.value = false
    }
}

// Password recovery handler
const handlePasswordRecovery = async () => {
    if (!isFormValid.value || props.disabled) return

    errors.value = []
    isLoading.value = true
    if (!await verifyRecaptcha()) return

    try {
        await resetPassword({
            email: formData.email,
            storefrontUrl
        })

        success(t('account.passwordRecovery.success.message'))
        closeSidenav('account')

        // Reset form and switch back to login mode
        formData.email = ''
        formData.password = ''
        isPasswordRecoveryMode.value = false

    } catch (error) {
        if (error instanceof ApiClientError) {
            errors.value = resolveApiErrors(error.details.errors)
        } else {
            errors.value = [error instanceof Error ? error.message : 'An unknown error occurred']
        }
    } finally {
        isLoading.value = false
    }
}

const togglePasswordRecoveryMode = () => {
    isPasswordRecoveryMode.value = !isPasswordRecoveryMode.value
    errors.value = []
    formData.password = ''
}

const handleFormSubmit = () => {
    if (isPasswordRecoveryMode.value) {
        handlePasswordRecovery()
    } else {
        handleLogin()
    }
}

// Clear errors when form data changes
watch(formData, () => {
    if (errors.value.length > 0) {
        errors.value = []
    }
})
</script>

<template>
    <div class="space-y-6">
        <ComponentFieldset
            :headline="isPasswordRecoveryMode ? $t('account.passwordRecovery.title') : $t('account.login.title')"
        >
            <form @submit.prevent="handleFormSubmit">
                <div class="space-y-4">
                    <div v-if="errors.length > 0" class="p-4 border-l-4 border-error bg-error/10 text-error" role="alert">
                        <p class="font-medium">{{ $t('account.login.error') }}</p>
                        <ul class="mt-2 list-disc list-inside">
                            <li v-for="error in errors" :key="error">{{ error }}</li>
                        </ul>
                    </div>

                    <ComponentTextInput
                        id="login-email"
                        v-model="formData.email"
                        :label="$t('form.email')"
                        :show-label="false"
                        type="email"
                        autocomplete="username"
                        :placeholder="$t('form.email')"
                        :disabled="isLoading"
                        size="md"
                        bordered
                    >
                        <template #iconAfter>
                            <FoundationIcon name="envelope" size="lg" class="mr-3" />
                        </template>
                    </ComponentTextInput>

                    <ComponentTextInput
                        v-if="!isPasswordRecoveryMode"
                        id="login-password"
                        v-model="formData.password"
                        :label="$t('form.password')"
                        :show-label="false"
                        type="password"
                        autocomplete="current-password"
                        :placeholder="$t('form.password')"
                        :disabled="isLoading"
                        size="md"
                        bordered
                    >
                        <template #iconAfter>
                            <FoundationIcon name="lock" size="lg" class="mr-3" />
                        </template>
                    </ComponentTextInput>

                    <FoundationLink
                        v-if="!isPasswordRecoveryMode"
                        href="#"
                        class="block text-sm cursor-pointer underline hover:no-underline font-bold"
                        @click.prevent="togglePasswordRecoveryMode"
                    >
                        {{ $t('account.login.forgotPassword') }}
                    </FoundationLink>

                    <FoundationLink
                        v-if="isPasswordRecoveryMode"
                        href="#"
                        class="block text-sm cursor-pointer underline hover:no-underline"
                        @click.prevent="togglePasswordRecoveryMode"
                    >
                        {{ $t('account.login.backToLogin') }}
                    </FoundationLink>

                    <FoundationButton
                        type="submit"
                        color="primary"
                        class="w-full"
                        :loading="isLoading"
                        :disabled="!isFormValid"
                        @click="handleFormSubmit"
                    >
                        {{ isPasswordRecoveryMode ? $t('account.passwordRecovery.submit') : $t('account.login.signIn') }}
                    </FoundationButton>
                </div>
            </form>
        </ComponentFieldset>

        <ComponentFieldset
            :headline="$t('account.registration.title')"
            :description="$t('account.registration.description')"
        >
            <FoundationButton
                v-if="!isPasswordRecoveryMode"
                type="button"
                color="primary"
                class="w-full"
                @click="$emit('switch-to-register')"
            >
                {{ $t('account.login.createAccount') }}
            </FoundationButton>
        </ComponentFieldset>
    </div>
</template>
